import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dxForm from 'devextreme/ui/form';
import { get, has } from 'lodash';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';
import type { Model } from '#/graphql/types';
import useModelUpdate from '../../hooks/useUpdateModelMutation';
import useModelCreate from '../../hooks/useCreateModelMutation';
import useModelDelete from '../../hooks/useDeleteModelMutation';
import { createFields, updateFields } from './fields';
import { inUseStatuses } from '../../utils/constants';
import allFields from '../../utils/fields';
import AttachmentsUpload from '#/common/components/AttachmentUpload';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { InitializedEventInfo } from 'devextreme/events';
import {
  getItemOptions,
  setEditorOptions,
  setItemOptions,
} from '#/common/components/Form/formItemOption';

export interface ModelFormProps {
  model: Model | {};
  title: string;
}

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: false,
  [UserRole.trane_admin]: false,
  [UserRole.technician]: true,
};

const ModelForm: React.FC<ModelFormProps> = ({ model, title }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const { user } = useSession();
  const role = user?.role as UserRole;

  const id = get(model, 'id', undefined);
  const fields = id ? updateFields : createFields;

  const { updateModel } = useModelUpdate();
  const { createModel } = useModelCreate();
  const { deleteModel } = useModelDelete();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteModel(id);
    },
    [id, deleteModel],
  );

  const submit = useCallback(
    (data: Model) => {
      if (!id) {
        return createModel(data);
      }
      return updateModel(id, data);
    },
    [id, updateModel, createModel],
  );

  const attachmentsDisabled = formReadonlyRole[role] || id === undefined;
  const isFormReadonly =
    formReadonlyRole[role] ||
    (has(model, 'thirdPartyId') &&
      has(model, 'thirdPartySource') &&
      !!(model.thirdPartyId && model.thirdPartySource));

  const actions = useMemo(() => {
    const result: FormAction<Model>[] = [];

    if (
      !isFormReadonly &&
      get(model, 'inUseStatus') === inUseStatuses.newlyCreated.key &&
      deleteItem
    ) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          if (!e.record?.id) {
            return;
          }

          const sure = await confirm(
            'error',
            `Are you sure you want to delete model: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteItem(e.record.id);
          e.setIsLoading(false);

          navigate('/assets/models');
        },
      });
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          if (
            e.record.inUseStatus === inUseStatuses.inUse.key &&
            has(model, 'inUseStatus') &&
            model.inUseStatus !== inUseStatuses.inUse.key
          ) {
            const sure = await confirm(
              'warning',
              'Once set to In-use, this Model cannot be deleted. Do you want to proceed?',
            );
            if (!sure) {
              return;
            }
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\\n`));
              return;
            }
            // patch returned data into the form so riskScore updates without refresh
            if (result?.data && e.form) {
              Object.entries(result.data).forEach(([key, value]) => {
                // updateData per field avoids re-dirtying the form on CIT (unlike option('formData', ...))
                e.form!.updateData(key, value);
              });
            }
            if (!id && result && result.data?.id) {
              navigate(`/assets/models/${result.data?.id}`, { replace: true });
            }

            e.clearDirty();
            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, model, deleteItem]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;

    if (!form) return;

    const formData = form.option('formData');

    // Dynamically patch image field to show model attachment
    const imageField = getItemOptions(form, 'picture');
    if (imageField?.editorOptions) {
      setItemOptions(form, 'picture', 'editorOptions', {
        ...imageField.editorOptions,
        relatedTable: 'models',
        relatedItemId: id ?? '',
        relatedField: 'picture',
        disableActions: attachmentsDisabled,
      });
    }

    if (formData?.inUseStatus !== inUseStatuses.newlyCreated.key) {
      const inUseStatusOptions = {
        dataSource: Object.values({
          ...inUseStatuses,
          newlyCreated: { ...inUseStatuses.newlyCreated, disabled: true },
        }),
      };
      setEditorOptions(form, 'inUseStatus', inUseStatusOptions);
    }
  }, []);
  return (
    <>
      <Form
        ref={formRef}
        supportTenant
        readonly={isFormReadonly}
        record={model as Model}
        fields={fields}
        actions={actions}
        onContentReady={onContentReady}
      />
      {id && (
        <AttachmentsUpload
          relatedTable="models"
          relatedItem={id}
          disableUpload={attachmentsDisabled}
          tenantId={get(model, 'tenantId')}
        />
      )}
    </>
  );
};

export default React.memo(ModelForm);
